import React, {Fragment, useEffect, useState} from 'react'
import {
    BarsArrowDownIcon,
    BarsArrowUpIcon,
    CreditCardIcon,
    NewspaperIcon,
    PencilIcon,
    XCircleIcon,
    XMarkIcon
} from '@heroicons/react/20/solid'
import {useNavigate} from "react-router-dom";
import {getVenueSession} from "../../requests/AppSession";
import dateFormat from "dateformat";
import {NumericFormat} from "react-number-format";
import {Skeleton} from "@mui/material";
import {Transition} from "@headlessui/react";
import {useAuth0} from "@auth0/auth0-react";
import {getVenueDashboard} from "../requests/AppRequestVenueDashboard";
import {createInvoicePaymentUrl} from "../requests/AppRequestStripeInvoicePayment";
import {CardGridSkeleton, TableRowsSkeleton} from "./VTSkeletons";

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function VTAdminDashboardNew2() {
    let navigate = useNavigate();

    const {isLoading, getAccessTokenSilently} = useAuth0();

    const [venueId, setVenueId] = React.useState(null);
    const [venueName, setVenueName] = React.useState(null);
    const [availableVTRemaining, setAvailableVTRemaining] = React.useState(null);
    const [invoices, setInvoices] = React.useState(null);
    const [showAllInvoices, setShowAllInvoices] = React.useState(false);
    const [buyHistory, setBuyHistory] = useState(null);
    const [tipHistory, setTipHistory] = useState(null);
    const [danceHistory, setDanceHistory] = useState(null);
    const [drinkHistory, setDrinkHistory] = useState(null);

    const [venueDashboardCashFlow, setVenueDashboardCashFlow] = React.useState(null);
    const [venueDashboardCashFlowDate, setVenueDashboardCashFlowDate] = React.useState("month");

    const [venueDashboardErrorMessage, setVenueDashboardErrorMessage] = React.useState(null);

    const [showBuyHistory, setShowBuyHistory] = React.useState(true);
    const [showTipHistory, setShowTipHistory] = React.useState(true);
    const [showDanceHistory, setShowDanceHistory] = React.useState(true);
    const [showDrinkHistory, setShowDrinkHistory] = React.useState(true);

    const [showError, setShowError] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [privateDancePayFeatureFlag, setPrivateDancePayFeatureFlag] = React.useState(false);
    const [dancePayFeatureFlag, setDancePayFeatureFlag] = React.useState(false);
    const [drinkPayFeatureFlag, setDrinkPayFeatureFlag] = React.useState(false);
    const [stageTipFeatureFlag, setStageTipFeatureFlag] = React.useState(false);
    const [venueRewardsFeatureFlag, setVenueRewardsFeatureFlag] = React.useState(false);
    const [venueStripePaymentsFeatureFlag, setVenueStripePaymentsFeatureFlag] = React.useState(false);

    const createPaymentUrlSuccess = (responseJson) => {
        setIsSubmitting(false);
        if (responseJson && responseJson.paymentUrl) {
            window.location.replace(responseJson.paymentUrl);
        }
    };

    const createPaymentUrlError = () => {
        setIsSubmitting(false);
        setShowError(true);
    };

    useEffect(() => {
        getVenueSession().then(venueSession => {
            if (venueSession && venueSession.venueId && venueSession.venueId !== null) {
                setVenueId(venueSession.venueId);
            } else {
                navigate('/login/mobile', {replace: true});
            }

            if (venueSession) {
                setPrivateDancePayFeatureFlag(venueSession.privateDancePayFeatureFlag);
                setDancePayFeatureFlag(venueSession.dancePayFeatureFlag);
                setDrinkPayFeatureFlag(venueSession.drinkPayFeatureFlag);
                setStageTipFeatureFlag(venueSession.stageTipFeatureFlag);
                setVenueRewardsFeatureFlag(venueSession.venueRewardsFeatureFlag);
                setVenueStripePaymentsFeatureFlag(venueSession.venueStripePaymentsFeatureFlag);
            } else {
                setPrivateDancePayFeatureFlag(false);
                setDancePayFeatureFlag(false);
                setDrinkPayFeatureFlag(false);
                setStageTipFeatureFlag(false);
                setVenueRewardsFeatureFlag(false);
                setVenueStripePaymentsFeatureFlag(false);
            }
        })
        return function cleanup() {

        };
    }, []);

    const getSuccess = async responseJson => {
        if (
            responseJson &&
            responseJson.id
        ) {
            setVenueName(responseJson.name);
            setAvailableVTRemaining(responseJson.availableVTRemaining);
            setInvoices(responseJson.venueInvoiceDtoList);
            setBuyHistory(responseJson.sellHistoryList);
            setTipHistory(responseJson.tipHistoryList);
            setDanceHistory(responseJson.danceHistoryList);
            setDrinkHistory(responseJson.drinkHistoryList);
            setVenueDashboardCashFlow(responseJson.venueDashboardCashFlow);
        } else {
            setVenueDashboardErrorMessage("Error getting Venue Dashboard information, please refresh and verify Internet Connection.");
        }
    };

    const getError = forceLogout => {
        setVenueDashboardErrorMessage("Error getting Venue Dashboard information, please refresh and verify Internet Connection.");
    };

    useEffect(() => {
        if (!isLoading && venueId !== null) {
            getVenueDashboard(venueId, getSuccess, getError, getAccessTokenSilently);
        }
        return function cleanup() {

        };
    }, [isLoading, venueId]);

    const getVenueDashboardCashFlowFromDate = (venueDashboardCashFlowData, venueDashboardCashFlowDateData) => {
        if (venueDashboardCashFlowDateData === "today") {
            return venueDashboardCashFlowData.today;
        } else if (venueDashboardCashFlowDateData === "yesterday") {
            return venueDashboardCashFlowData.yesterday;
        }
        return venueDashboardCashFlowData.thisMonth;
    }

    return (
        <>
            <main>
                <div className="relative isolate overflow-hidden pt-16">
                    {/* Secondary navigation */}
                    <header className="pb-4 pt-6 sm:pb-6">
                        <div
                            className="mx-auto flex max-w-7xl flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
                            <h1 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">Cashflow From VirtualTipz
                                Sells</h1>
                            <div
                                className="order-last flex w-full gap-x-8 text-sm font-semibold leading-6 sm:order-none sm:w-auto sm:border-l sm:border-gray-200 dark:sm:border-gray-700 sm:pl-6 sm:leading-7">
                                {/*<button*/}
                                {/*    className={venueDashboardCashFlowDate === "today" ? 'text-pink-600' : 'text-gray-700'}*/}
                                {/*    onClick={() => {*/}
                                {/*        setVenueDashboardCashFlowDate("today");*/}
                                {/*    }}*/}
                                {/*>*/}
                                {/*    Today*/}
                                {/*</button>*/}
                                {/*<button*/}
                                {/*    className={venueDashboardCashFlowDate === "yesterday" ? 'text-pink-600' : 'text-gray-700'}*/}
                                {/*    onClick={() => {*/}
                                {/*        setVenueDashboardCashFlowDate("yesterday");*/}
                                {/*    }}*/}
                                {/*>*/}
                                {/*    Yesterday*/}
                                {/*</button>*/}
                                <button
                                    className={venueDashboardCashFlowDate === "month" ? 'text-pink-600' : 'text-gray-700 dark:text-gray-200'}
                                    onClick={() => {
                                        setVenueDashboardCashFlowDate("month");
                                    }}
                                >
                                    This Month
                                </button>
                            </div>
                            <button
                                className="ml-auto flex items-center gap-x-1 rounded-md bg-pink-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-pink-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600"
                                onClick={() => {
                                    navigate('/venue/edit');
                                }}
                            >
                                <PencilIcon className="-ml-1.5 h-5 w-5" aria-hidden="true"/>
                                Edit Venue
                            </button>
                        </div>
                    </header>

                    {/* Stats */}
                    <div className="border-b border-b-gray-900/10 lg:border-t lg:border-t-gray-900/5">
                        <dl className="mx-auto grid max-w-7xl grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 lg:px-2 xl:px-0">
                            <div
                                className={classNames(
                                    'flex items-baseline flex-wrap justify-between gap-y-2 gap-x-4 border-t border-gray-900/5 px-4 py-10 sm:px-6 lg:border-t-0 xl:px-8'
                                )}
                            >
                                <dt className="text-sm font-medium leading-6 text-gray-500 dark:text-gray-400">Venue Gross</dt>
                                <dd className="w-full flex-none text-3xl font-medium leading-10 tracking-tight text-gray-900 dark:text-gray-100">
                                    {
                                        venueDashboardCashFlow ?
                                            <NumericFormat
                                                value={venueDashboardCashFlow ? getVenueDashboardCashFlowFromDate(venueDashboardCashFlow, venueDashboardCashFlowDate).totalRevenue : 0}
                                                displayType={'text'}
                                                decimalScale={2}
                                                fixedDecimalScale={true}
                                                thousandSeparator={true}
                                                prefix={'$'}/>
                                            :
                                            <Skeleton variant="rounded"/>
                                    }
                                </dd>
                            </div>
                            {/*<div*/}
                            {/*    className={classNames(*/}
                            {/*        'sm:border-l',*/}
                            {/*        'flex items-baseline flex-wrap justify-between gap-y-2 gap-x-4 border-t border-gray-900/5 px-4 py-10 sm:px-6 lg:border-t-0 xl:px-8'*/}
                            {/*    )}*/}
                            {/*>*/}
                            {/*    <dt className="text-sm font-medium leading-6 text-gray-500 dark:text-gray-400">Total VirtualTipz</dt>*/}
                            {/*    <dd*/}
                            {/*        className={classNames(*/}
                            {/*            'text-green-700',*/}
                            {/*            'text-xs font-medium'*/}
                            {/*        )}*/}
                            {/*    >*/}
                            {/*        {*/}
                            {/*            venueDashboardCashFlow && getVenueDashboardCashFlowFromDate(venueDashboardCashFlow, venueDashboardCashFlowDate).tipProfit > 0 ?*/}
                            {/*                <>*/}
                            {/*                    Venue Profit <NumericFormat*/}
                            {/*                    value={venueDashboardCashFlow ? getVenueDashboardCashFlowFromDate(venueDashboardCashFlow, venueDashboardCashFlowDate).tipProfit : 0}*/}
                            {/*                    displayType={'text'}*/}
                            {/*                    decimalScale={2}*/}
                            {/*                    fixedDecimalScale={true}*/}
                            {/*                    thousandSeparator={true}*/}
                            {/*                    prefix={'$'}/>*/}
                            {/*                    <br/>*/}
                            {/*                    Dancer Profit <NumericFormat*/}
                            {/*                    value={venueDashboardCashFlow ? getVenueDashboardCashFlowFromDate(venueDashboardCashFlow, venueDashboardCashFlowDate).tipMoney - getVenueDashboardCashFlowFromDate(venueDashboardCashFlow, venueDashboardCashFlowDate).tipProfit : 0}*/}
                            {/*                    displayType={'text'}*/}
                            {/*                    decimalScale={2}*/}
                            {/*                    fixedDecimalScale={true}*/}
                            {/*                    thousandSeparator={true}*/}
                            {/*                    prefix={'$'}/>*/}
                            {/*                </>*/}
                            {/*                :*/}
                            {/*                <></>*/}
                            {/*        }*/}
                            {/*    </dd>*/}
                            {/*    <dd className="w-full flex-none text-3xl font-medium leading-10 tracking-tight text-gray-900 dark:text-gray-100">*/}
                            {/*        {*/}
                            {/*            venueDashboardCashFlow ?*/}
                            {/*                <NumericFormat*/}
                            {/*                    value={venueDashboardCashFlow ? getVenueDashboardCashFlowFromDate(venueDashboardCashFlow, venueDashboardCashFlowDate).tipMoney : 0}*/}
                            {/*                    displayType={'text'}*/}
                            {/*                    // decimalScale={2}*/}
                            {/*                    fixedDecimalScale={true}*/}
                            {/*                    thousandSeparator={true}*/}
                            {/*                    suffix={' VT '}*/}
                            {/*                    prefix={''}/>*/}

                            {/*                :*/}
                            {/*                <Skeleton variant="rounded"/>*/}
                            {/*        }*/}
                            {/*    </dd>*/}
                            {/*</div>*/}
                            <div
                                className={classNames(
                                    'sm:border-l',
                                    'flex items-baseline flex-wrap justify-between gap-y-2 gap-x-4 border-t border-gray-900/5 px-4 py-10 sm:px-6 lg:border-t-0 xl:px-8'
                                )}
                            >
                                <dt className="text-sm font-medium leading-6 text-gray-500 dark:text-gray-400">Venue Profit</dt>
                                <dd className="w-full flex-none text-4xl font-medium leading-10 tracking-tight text-gray-900 dark:text-gray-100">
                                    {
                                        venueDashboardCashFlow ?
                                            <NumericFormat
                                                value={venueDashboardCashFlow ? getVenueDashboardCashFlowFromDate(venueDashboardCashFlow, venueDashboardCashFlowDate).profit : 0}
                                                displayType={'text'}
                                                decimalScale={2}
                                                fixedDecimalScale={true}
                                                thousandSeparator={true}
                                                prefix={'$'}/>
                                            :
                                            <Skeleton variant="rounded"/>
                                    }
                                </dd>
                            </div>
                            <div
                                className={classNames(
                                    'lg:border-l',
                                    'flex items-baseline flex-wrap justify-between gap-y-2 gap-x-4 border-t border-gray-900/5 px-4 py-10 sm:px-6 lg:border-t-0 xl:px-8'
                                )}
                            >
                                <dt className="text-sm font-medium leading-6 text-gray-500 dark:text-gray-400">Venue Reserve</dt>
                                <dd className="w-full flex-none text-3xl font-medium leading-10 tracking-tight text-gray-900 dark:text-gray-100">
                                    {
                                        venueDashboardCashFlow ?
                                            <NumericFormat
                                                value={venueDashboardCashFlow ? availableVTRemaining : 0}
                                                displayType={'text'}
                                                // decimalScale={2}
                                                fixedDecimalScale={true}
                                                thousandSeparator={true}
                                                suffix={' VT '}
                                                prefix={''}/>
                                            :
                                            <Skeleton variant="rounded"/>
                                    }
                                </dd>
                            </div>
                            {(stageTipFeatureFlag || dancePayFeatureFlag || privateDancePayFeatureFlag) && <div
                                className={classNames(
                                    'lg:border-l',
                                    'flex items-baseline flex-wrap justify-between gap-y-2 gap-x-4 border-t border-gray-900/5 px-4 py-10 sm:px-6 lg:border-t-0 xl:px-8'
                                )}
                            >
                                <dt className="text-sm font-medium leading-6 text-gray-500 dark:text-gray-400">Dancer Profits</dt>
                                <dd className="w-full flex-none text-3xl font-medium leading-10 tracking-tight text-gray-900 dark:text-gray-100">
                                    {
                                        venueDashboardCashFlow ?
                                            <NumericFormat
                                                value={venueDashboardCashFlow ? getVenueDashboardCashFlowFromDate(venueDashboardCashFlow, venueDashboardCashFlowDate).dancersProfit : 0}
                                                displayType={'text'}
                                                decimalScale={2}
                                                fixedDecimalScale={true}
                                                thousandSeparator={true}
                                                prefix={'$'}/>
                                            :
                                            <Skeleton variant="rounded"/>
                                    }
                                </dd>
                            </div>}
                            <div
                                className={classNames(
                                    'lg:border-l',
                                    'flex items-baseline flex-wrap justify-between gap-y-2 gap-x-4 border-t border-gray-900/5 px-4 py-10 sm:px-6 lg:border-t-0 xl:px-8'
                                )}
                            >
                                <dt className="text-sm font-medium leading-6 text-gray-500 dark:text-gray-400">Upcoming Invoice</dt>
                                <dd className="w-full flex-none text-3xl font-medium leading-10 tracking-tight text-gray-900 dark:text-gray-100">
                                    {
                                        venueDashboardCashFlow ?
                                            <NumericFormat
                                                value={venueDashboardCashFlow ? getVenueDashboardCashFlowFromDate(venueDashboardCashFlow, venueDashboardCashFlowDate).invoiceAmount : 0}
                                                displayType={'text'}
                                                decimalScale={2}
                                                fixedDecimalScale={true}
                                                thousandSeparator={true}
                                                prefix={'$'}/>
                                            :
                                            <Skeleton variant="rounded"/>
                                    }
                                </dd>
                            </div>
                        </dl>
                    </div>

                    <div
                        className="absolute left-0 top-full -z-10 mt-96 origin-top-left translate-y-40 -rotate-90 transform-gpu opacity-20 blur-3xl sm:left-1/2 sm:-ml-96 sm:-mt-10 sm:translate-y-0 sm:rotate-0 sm:transform-gpu sm:opacity-50"
                        aria-hidden="true"
                    >
                        <div
                            className="aspect-[1154/678] w-[72.125rem] bg-gradient-to-br from-[#FF80B5] to-[#9089FC]"
                            style={{
                                clipPath:
                                    'polygon(100% 38.5%, 82.6% 100%, 60.2% 37.7%, 52.4% 32.1%, 47.5% 41.8%, 45.2% 65.6%, 27.5% 23.4%, 0.1% 35.3%, 17.9% 0%, 27.7% 23.4%, 76.2% 2.5%, 74.2% 56%, 100% 38.5%)',
                            }}
                        />
                    </div>
                </div>

                <div className="space-y-16 py-16 xl:space-y-20">
                    {/* Recent client list*/}
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
                            <div className="flex items-center justify-between">
                                {showAllInvoices ?
                                    <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">Unpaid & Paid
                                        Invoices</h2> :
                                    <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">Unpaid
                                        Invoices</h2>}
                                {showAllInvoices ? <button
                                    className="text-sm font-semibold leading-6 text-pink-600 hover:text-pink-500"
                                    onClick={() => {
                                        setShowAllInvoices(false);
                                    }}
                                >
                                    View Unpaid Only
                                </button> : <button
                                    className="text-sm font-semibold leading-6 text-pink-600 hover:text-pink-500"
                                    onClick={() => {
                                        setShowAllInvoices(true);
                                    }}
                                >
                                    View Unpaid & Paid
                                </button>}
                            </div>
                            {!invoices && <CardGridSkeleton/>}
                            <ul role="list" className="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 lg:grid-cols-3 xl:gap-x-8">
                                {invoices && (showAllInvoices ? invoices.length === 0 : invoices.filter((invoice) => !invoice.paid).length === 0) &&
                                <div className="py-5 text-gray-900 dark:text-gray-100">{showAllInvoices ? "No Unpaid & Paid Invoices" : "No Unpaid Invoices"}</div>}
                                {invoices && invoices.map((invoice) => (
                                    invoice.paid && !showAllInvoices ? <></> : <li key={invoice.id}
                                                                                   className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                                        <dl className="-my-3 divide-y divide-gray-100 dark:divide-gray-700 px-6 py-4 text-sm leading-6">
                                            <div className="flex justify-between gap-x-4 py-3">
                                                <dt className="text-gray-500 dark:text-gray-400">Due By</dt>
                                                <dd className="text-gray-700 dark:text-gray-200">
                                                    {dateFormat(invoice.payBy, "mmmm dS, yyyy")}
                                                </dd>
                                            </div>
                                            <div className="flex justify-between gap-x-4 py-3">
                                                <dt className="text-gray-500 dark:text-gray-400">Amount Owed</dt>
                                                <dd className="flex items-start gap-x-2">
                                                    <div
                                                        className="font-medium text-gray-900 dark:text-gray-100">{<NumericFormat
                                                        value={invoice.totalAmount} displayType={'text'}
                                                        decimalScale={2} fixedDecimalScale={true}
                                                        thousandSeparator={true} prefix={'$'}/>}</div>
                                                    {invoice.paid ? <div
                                                            className={'text-green-700 bg-green-50 ring-green-600/20 rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset'}
                                                        >
                                                            Paid
                                                        </div> :
                                                        <div
                                                            className={'text-red-700 bg-red-50 ring-red-600/10 rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset'}
                                                        >
                                                            Due
                                                        </div>
                                                    }
                                                </dd>
                                            </div>
                                            <div className="flex justify-between gap-x-4 py-3">

                                                {invoice.paid ?
                                                    <div
                                                        className={'text-green-700 bg-green-50 ring-green-600/20 rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset'}
                                                    >
                                                        Paid on {dateFormat(invoice.paidDate, "mmmm dS, yyyy")}
                                                    </div>
                                                    : <button
                                                        type="button"
                                                        disabled={isSubmitting}
                                                        className="inline-flex items-center gap-x-1.5 rounded-md bg-green-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
                                                        onClick={() => {
                                                            setIsSubmitting(true);
                                                            createInvoicePaymentUrl(venueId, invoice.id, createPaymentUrlSuccess, createPaymentUrlError, getAccessTokenSilently);
                                                        }}
                                                    >
                                                        Pay Invoice
                                                        <CreditCardIcon className="-mr-0.5 h-5 w-5" aria-hidden="true"/>
                                                    </button>}
                                                <dd className="flex items-start gap-x-2">
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center gap-x-1.5 rounded-md bg-pink-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-pink-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600"
                                                        onClick={() => {
                                                            navigate("/venue/invoice/details", {state: {invoiceId: invoice.id}});

                                                        }}
                                                    >
                                                        View Details
                                                        <NewspaperIcon className="-mr-0.5 h-5 w-5" aria-hidden="true"/>
                                                    </button>
                                                </dd>
                                            </div>
                                        </dl>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Recent Sell Activity table */}
                    <div>
                        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                            <div className="flex items-center justify-between">
                                <div className="flex">
                                    {showBuyHistory ?
                                        <span className="flex cursor-pointer" onClick={() => {
                                            setShowBuyHistory(false);
                                        }}>
                                            <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">Sell Activity</h2>
                                            <BarsArrowUpIcon
                                                className="h-5 w-5" aria-hidden="true"/>
                                        </span> :
                                        <span className="flex cursor-pointer" onClick={() => {
                                            setShowBuyHistory(true);
                                        }}>
                                            <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">Sell Activity</h2>
                                            <BarsArrowDownIcon
                                                className="h-5 w-5" aria-hidden="true"/>
                                        </span>
                                    }
                                </div>

                                <button
                                    className="text-sm font-semibold leading-6 text-pink-600 hover:text-pink-500"
                                    onClick={() => {
                                        navigate('/venue/sell/history');
                                    }}
                                >
                                    View all<span className="sr-only">, sell</span>
                                </button>
                            </div>
                        </div>
                        <div className="mt-6 overflow-hidden border-t border-gray-100 dark:border-gray-700">
                            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                                <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
                                    <table className="w-full text-left">
                                        <thead className="sr-only">
                                        <tr>
                                            <th>Amount</th>
                                            <th className="hidden sm:table-cell">Client</th>
                                            <th>More details</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {!buyHistory && <TableRowsSkeleton rows={3} cols={2}/>}
                                        {showBuyHistory && <Fragment>
                                            {buyHistory && <tr className="text-sm leading-6 text-gray-900 dark:text-gray-100">
                                                <th scope="colgroup" colSpan={3}
                                                    className="relative isolate py-2 font-semibold">
                                                    <span>Most Recent</span>
                                                    <div
                                                        className="absolute inset-y-0 right-full -z-10 w-screen border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"/>
                                                    <div
                                                        className="absolute inset-y-0 left-0 -z-10 w-screen border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"/>
                                                </th>
                                            </tr>}
                                            {buyHistory && buyHistory.length === 0 &&
                                                <div className="py-5 text-gray-900 dark:text-gray-100">No Recent Sell Activity</div>}
                                            {buyHistory && buyHistory.map((history) => (
                                                <tr key={history.id}>
                                                    <td className="relative py-5 pr-6">
                                                        <div className="flex gap-x-6">
                                                            <div className="flex-auto">
                                                                <div className="flex items-start gap-x-3">
                                                                    <div
                                                                        className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                                        {history.packageName} ({history.virtualTips} VT)
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                                                                    <NumericFormat value={history.price}
                                                                                   displayType={'text'}
                                                                                   decimalScale={2}
                                                                                   fixedDecimalScale={true}
                                                                                   thousandSeparator={true}
                                                                                   prefix={'$'}/>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="absolute bottom-0 right-full h-px w-screen bg-gray-100 dark:bg-gray-800"/>
                                                        <div
                                                            className="absolute bottom-0 left-0 h-px w-screen bg-gray-100 dark:bg-gray-800"/>
                                                    </td>
                                                    <td className="py-5 text-right">
                                                        <div className="flex justify-end">
                                                            <span
                                                                className="text-sm font-medium leading-6 text-pink-600 hover:text-pink-500"
                                                            >
                                                                Venue Profit <NumericFormat
                                                                value={history.amountToVenue}
                                                                displayType={'text'}
                                                                decimalScale={2}
                                                                fixedDecimalScale={true}
                                                                thousandSeparator={true}
                                                                prefix={'$'}/>
                                                                <span
                                                                    className="hidden sm:inline"> </span>
                                                            </span>
                                                        </div>
                                                        <div className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                                                                <span
                                                                    className="text-gray-900 dark:text-gray-100">{dateFormat(history.purchaseDate, "mmmm dS, yyyy")}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </Fragment>}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recent Tip Activity table */}
                    {stageTipFeatureFlag && <div>
                        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                            <div className="flex items-center justify-between">
                                <div className="flex">
                                    {showTipHistory ?
                                        <span className="flex cursor-pointer" onClick={() => {
                                            setShowTipHistory(false);
                                        }}>
                                            <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">Tip Activity</h2>
                                            <BarsArrowUpIcon
                                                className="h-5 w-5" aria-hidden="true"/>
                                        </span> :
                                        <span className="flex cursor-pointer" onClick={() => {
                                            setShowTipHistory(true);
                                        }}>
                                            <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">Tip Activity</h2>
                                            <BarsArrowDownIcon
                                                className="h-5 w-5" aria-hidden="true"/>
                                        </span>
                                    }
                                </div>

                                <button
                                    className="text-sm font-semibold leading-6 text-pink-600 hover:text-pink-500"
                                    onClick={() => {
                                        navigate('/venue/tip/history');
                                    }}
                                >
                                    View all<span className="sr-only">, tip</span>
                                </button>
                            </div>
                        </div>
                        <div className="mt-6 overflow-hidden border-t border-gray-100 dark:border-gray-700">
                            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                                <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
                                    <table className="w-full text-left">
                                        <thead className="sr-only">
                                        <tr>
                                            <th>Amount</th>
                                            <th className="hidden sm:table-cell">Client</th>
                                            <th>More details</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {!tipHistory && <TableRowsSkeleton rows={3} cols={2}/>}
                                        {showTipHistory && <Fragment>
                                            {tipHistory && <tr className="text-sm leading-6 text-gray-900 dark:text-gray-100">
                                                <th scope="colgroup" colSpan={3}
                                                    className="relative isolate py-2 font-semibold">
                                                    <span>Most Recent</span>
                                                    <div
                                                        className="absolute inset-y-0 right-full -z-10 w-screen border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"/>
                                                    <div
                                                        className="absolute inset-y-0 left-0 -z-10 w-screen border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"/>
                                                </th>
                                            </tr>}
                                            {tipHistory && tipHistory.length === 0 &&
                                                <div className="py-5 text-gray-900 dark:text-gray-100">No Recent Tip Activity</div>}
                                            {tipHistory && tipHistory.map((tip) => (
                                                <tr key={tip.id}>
                                                    <td className="relative py-5 pr-6">
                                                        <div className="flex gap-x-6">
                                                            <div className="flex-auto">
                                                                <div className="flex items-start gap-x-3">
                                                                    <div
                                                                        className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                                        {tip.dancerName} (Dancer Profit: {<NumericFormat
                                                                        value={tip.amountToDancer}
                                                                        displayType={'text'}
                                                                        decimalScale={2}
                                                                        fixedDecimalScale={true}
                                                                        thousandSeparator={true}
                                                                        prefix={'$'}/>})
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                                                                    <NumericFormat value={tip.tipAmount}
                                                                                   displayType={'text'}
                                                                                   decimalScale={2}
                                                                                   fixedDecimalScale={true}
                                                                                   thousandSeparator={true}
                                                                                   prefix={'$'}/>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="absolute bottom-0 right-full h-px w-screen bg-gray-100 dark:bg-gray-800"/>
                                                        <div
                                                            className="absolute bottom-0 left-0 h-px w-screen bg-gray-100 dark:bg-gray-800"/>
                                                    </td>

                                                    <td className="py-5 text-right">
                                                        <div className="flex justify-end">
                                                                <span
                                                                    className="text-sm font-medium leading-6 text-pink-600 hover:text-pink-500"
                                                                >
                                                                    Venue Profit <NumericFormat
                                                                    value={tip.amountToVenue}
                                                                    displayType={'text'}
                                                                    decimalScale={2}
                                                                    fixedDecimalScale={true}
                                                                    thousandSeparator={true}
                                                                    prefix={'$'}/>
                                                                    <span
                                                                        className="hidden sm:inline"> </span>
                                                                </span>
                                                        </div>
                                                        <div className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                                                                <span
                                                                    className="text-gray-900 dark:text-gray-100">{dateFormat(tip.createDate, "mmmm dS, yyyy")}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </Fragment>}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>}

                    {/* Recent Dance Activity */}
                    {(dancePayFeatureFlag || privateDancePayFeatureFlag) && <div>
                        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                            <div className="flex items-center justify-between">
                                <div className="flex">
                                    {showDanceHistory ?
                                        <span className="flex cursor-pointer" onClick={() => {
                                            setShowDanceHistory(false);
                                        }}>
                                            <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">Dance Activity</h2>
                                            <BarsArrowUpIcon
                                                className="h-5 w-5" aria-hidden="true"/>
                                        </span> :
                                        <span className="flex cursor-pointer" onClick={() => {
                                            setShowDanceHistory(true);
                                        }}>
                                            <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">Dance Activity</h2>
                                            <BarsArrowDownIcon
                                                className="h-5 w-5" aria-hidden="true"/>
                                        </span>
                                    }
                                </div>

                                <button
                                    className="text-sm font-semibold leading-6 text-pink-600 hover:text-pink-500"
                                    onClick={() => {
                                        navigate('/venue/dance/history');
                                    }}
                                >
                                    View all<span className="sr-only">, dance</span>
                                </button>
                            </div>
                        </div>
                        <div className="mt-6 overflow-hidden border-t border-gray-100 dark:border-gray-700">
                            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                                <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
                                    <table className="w-full text-left">
                                        <thead className="sr-only">
                                        <tr>
                                            <th>Amount</th>
                                            <th className="hidden sm:table-cell">Client</th>
                                            <th>More details</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {!danceHistory && <TableRowsSkeleton rows={3} cols={2}/>}
                                        {showDanceHistory && <Fragment>
                                            {danceHistory && <tr className="text-sm leading-6 text-gray-900 dark:text-gray-100">
                                                <th scope="colgroup" colSpan={3}
                                                    className="relative isolate py-2 font-semibold">
                                                    <span>Most Recent</span>
                                                    <div
                                                        className="absolute inset-y-0 right-full -z-10 w-screen border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"/>
                                                    <div
                                                        className="absolute inset-y-0 left-0 -z-10 w-screen border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"/>
                                                </th>
                                            </tr>}
                                            {danceHistory && danceHistory.length === 0 &&
                                                <div className="py-5 text-gray-900 dark:text-gray-100">No Recent Dance Activity</div>}
                                            {danceHistory && danceHistory.map((dance) => (
                                                <tr key={dance.id}>
                                                    <td className="relative py-5 pr-6">
                                                        <div className="flex gap-x-6">
                                                            <div className="flex-auto">
                                                                <div className="flex items-start gap-x-3">
                                                                    <div
                                                                        className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                                        {dance.packageName} ({dance.dancerName})
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                                                                    <NumericFormat value={dance.vtAmount}
                                                                                   displayType={'text'}
                                                                                   decimalScale={2}
                                                                                   fixedDecimalScale={true}
                                                                                   thousandSeparator={true}
                                                                                   prefix={'$'}/>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="absolute bottom-0 right-full h-px w-screen bg-gray-100 dark:bg-gray-800"/>
                                                        <div
                                                            className="absolute bottom-0 left-0 h-px w-screen bg-gray-100 dark:bg-gray-800"/>
                                                    </td>

                                                    <td className="py-5 text-right">
                                                        <div className="flex justify-end">
                                                                <span
                                                                    className="text-sm font-medium leading-6 text-pink-600 hover:text-pink-500"
                                                                >
                                                                    Venue Profit <NumericFormat
                                                                    value={dance.amountToVenue}
                                                                    displayType={'text'}
                                                                    decimalScale={2}
                                                                    fixedDecimalScale={true}
                                                                    thousandSeparator={true}
                                                                    prefix={'$'}/>
                                                                    <span
                                                                        className="hidden sm:inline"> </span>
                                                                </span>
                                                        </div>
                                                        <div className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                                                                <span
                                                                    className="text-gray-900 dark:text-gray-100">{dateFormat(dance.createDate, "mmmm dS, yyyy")}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </Fragment>}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>}

                    {/* Recent Drink Activity */}
                    {drinkPayFeatureFlag && <div>
                        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                            <div className="flex items-center justify-between">
                                <div className="flex">
                                    {showDrinkHistory ?
                                        <span className="flex cursor-pointer" onClick={() => {
                                            setShowDrinkHistory(false);
                                        }}>
                                            <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">Drink Activity</h2>
                                            <BarsArrowUpIcon
                                                className="h-5 w-5" aria-hidden="true"/>
                                        </span> :
                                        <span className="flex cursor-pointer" onClick={() => {
                                            setShowDrinkHistory(true);
                                        }}>
                                            <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">Drink Activity</h2>
                                            <BarsArrowDownIcon
                                                className="h-5 w-5" aria-hidden="true"/>
                                        </span>
                                    }
                                </div>

                                <button
                                    className="text-sm font-semibold leading-6 text-pink-600 hover:text-pink-500"
                                    onClick={() => {
                                        navigate('/venue/drink/history');
                                    }}
                                >
                                    View all<span className="sr-only">, drink</span>
                                </button>
                            </div>
                        </div>
                        <div className="mt-6 overflow-hidden border-t border-gray-100 dark:border-gray-700">
                            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                                <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
                                    <table className="w-full text-left">
                                        <thead className="sr-only">
                                        <tr>
                                            <th>Amount</th>
                                            <th className="hidden sm:table-cell">Client</th>
                                            <th>More details</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {!drinkHistory && <TableRowsSkeleton rows={3} cols={2}/>}
                                        {showDrinkHistory && <Fragment>
                                            {drinkHistory && <tr className="text-sm leading-6 text-gray-900 dark:text-gray-100">
                                                <th scope="colgroup" colSpan={3}
                                                    className="relative isolate py-2 font-semibold">
                                                    <span>Most Recent</span>
                                                    <div
                                                        className="absolute inset-y-0 right-full -z-10 w-screen border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"/>
                                                    <div
                                                        className="absolute inset-y-0 left-0 -z-10 w-screen border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"/>
                                                </th>
                                            </tr>}
                                            {drinkHistory && drinkHistory.length === 0 &&
                                                <div className="py-5 text-gray-900 dark:text-gray-100">No Recent Drink Activity</div>}
                                            {drinkHistory && drinkHistory.map((drink) => (
                                                <tr key={drink.id}>
                                                    <td className="relative py-5 pr-6">
                                                        <div className="flex gap-x-6">
                                                            <div className="flex-auto">
                                                                <div className="flex items-start gap-x-3">
                                                                    <div
                                                                        className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                                        {drink.drinkName}
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                                                                    <NumericFormat value={drink.vtAmount}
                                                                                   displayType={'text'}
                                                                                   decimalScale={0}
                                                                                   fixedDecimalScale={true}
                                                                                   thousandSeparator={true}
                                                                    /> {' '} VT
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="absolute bottom-0 right-full h-px w-screen bg-gray-100 dark:bg-gray-800"/>
                                                        <div
                                                            className="absolute bottom-0 left-0 h-px w-screen bg-gray-100 dark:bg-gray-800"/>
                                                    </td>

                                                    <td className="py-5 text-right">
                                                        <div className="flex justify-end">
                                                                <span
                                                                    className="text-sm font-medium leading-6 text-pink-600 hover:text-pink-500"
                                                                >
                                                                    Venue Profit <NumericFormat
                                                                    value={drink.amountToVenue}
                                                                    displayType={'text'}
                                                                    decimalScale={0}
                                                                    fixedDecimalScale={true}
                                                                    thousandSeparator={true}
                                                                    prefix={'$'}/>
                                                                    <span
                                                                        className="hidden sm:inline"> </span>
                                                                </span>
                                                        </div>
                                                        <div className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                                                                <span
                                                                    className="text-gray-900 dark:text-gray-100">{dateFormat(drink.createDate, "mmmm dS, yyyy")}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </Fragment>}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>}
                </div>
            </main>

            {/* Global notification live region, render this permanently at the end of the document */}
            <div
                aria-live="assertive"
                className="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6"
            >
                <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
                    {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
                    <Transition
                        show={showError}
                        as={Fragment}
                        enter="transform ease-out duration-300 transition"
                        enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
                        enterTo="translate-y-0 opacity-100 sm:translate-x-0"
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div
                            className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5">
                            <div className="p-4">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <XCircleIcon className="h-6 w-6 text-red-700" aria-hidden="true"/>
                                    </div>
                                    <div className="ml-3 w-0 flex-1 pt-0.5">
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Paying Invoice failed, please
                                            try again!</p>
                                    </div>
                                    <div className="ml-4 flex flex-shrink-0">
                                        <button
                                            type="button"
                                            className="inline-flex rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-green-700 focus:ring-offset-2"
                                            onClick={() => {
                                                setShowError(false)
                                            }}
                                        >
                                            <span className="sr-only">Close</span>
                                            <XMarkIcon className="h-5 w-5" aria-hidden="true"/>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>
        </>
    )
}
